import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, ArrowUpRight, BarChart3,
  Map as MapIcon, Landmark, GraduationCap, Dumbbell, Bus,
  TreePine, ShoppingBag, UtensilsCrossed, Palette, BookOpen,
  ChevronDown, ChevronUp, ChevronRight, ArrowRight, Calendar, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => new Intl.NumberFormat('fr-DZ').format(v);

// Color scale: green (cheap) → yellow → orange → red (expensive)
const priceToColor = (price, min, max) => {
  if (max === min) return '#f59e0b';
  const ratio = Math.max(0, Math.min(1, (price - min) / (max - min)));
  // 5-step gradient: green → teal → yellow → orange → red
  const stops = [
    { r: 34, g: 197, b: 94 },   // green-500
    { r: 20, g: 184, b: 166 },  // teal-500
    { r: 234, g: 179, b: 8 },   // yellow-500
    { r: 249, g: 115, b: 22 },  // orange-500
    { r: 239, g: 68, b: 68 },   // red-500
  ];
  const idx = ratio * (stops.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.min(lo + 1, stops.length - 1);
  const t = idx - lo;
  const r = Math.round(stops[lo].r + (stops[hi].r - stops[lo].r) * t);
  const g = Math.round(stops[lo].g + (stops[hi].g - stops[lo].g) * t);
  const b = Math.round(stops[lo].b + (stops[hi].b - stops[lo].b) * t);
  return `rgb(${r},${g},${b})`;
};

// Color-coded price bubble markers
const CityBubbles = ({ cities, mode, stats }) => {
  const map = useMap();

  useEffect(() => {
    const markers = [];
    const min = mode === 'sale' ? stats.min_sale : stats.min_rent;
    const max = mode === 'sale' ? stats.max_sale : stats.max_rent;

    cities.forEach((c) => {
      const price = mode === 'sale' ? c.avg_price_sqm_sale : c.avg_price_sqm_rent;
      const label = fmt(price);
      const color = priceToColor(price, min, max);

      const icon = L.divIcon({
        className: 'price-bubble-marker',
        html: `
          <div style="
            display:flex;flex-direction:column;align-items:center;
            cursor:pointer;transition:transform .2s;
          ">
            <div style="
              background:${color};
              color:#fff;
              padding:7px 14px;
              border-radius:20px;
              font-size:13px;
              font-weight:700;
              white-space:nowrap;
              box-shadow:0 3px 12px ${color}66;
              border:2.5px solid rgba(255,255,255,0.7);
              font-family:'Manrope',sans-serif;
              text-shadow:0 1px 2px rgba(0,0,0,0.2);
            ">${label}</div>
            <div style="
              font-size:11px;
              font-weight:700;
              color:#1e293b;
              margin-top:3px;
              text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;
              font-family:'Manrope',sans-serif;
            ">${c.city}</div>
          </div>
        `,
        iconSize: [100, 50],
        iconAnchor: [50, 25],
      });

      const marker = L.marker([c.latitude, c.longitude], { icon }).addTo(map);

      const neighborhoods = c.neighborhoods || [];
      let html = `<div style="font-family:'Manrope',sans-serif;min-width:230px">
        <h3 style="font-size:15px;font-weight:700;color:#0c2d57;margin:0 0 8px">${c.city}</h3>
        <div style="font-size:13px;color:#475569;margin-bottom:8px">
          ${mode === 'sale' ? 'Prix moyen vente' : 'Loyer moyen'}: <strong style="color:${color}">${label} DA/m²</strong>
        </div>`;
      if (neighborhoods.length > 0) {
        html += `<table style="width:100%;font-size:12px;border-collapse:collapse">
          <thead><tr style="border-bottom:1px solid #e2e8f0">
            <th style="text-align:left;padding:3px 6px;color:#64748b">Quartier</th>
            <th style="text-align:right;padding:3px 6px;color:#64748b">DA/m²</th>
            <th style="text-align:center;padding:3px 6px;color:#64748b">Tend.</th>
          </tr></thead><tbody>`;
        neighborhoods.forEach((n) => {
          const np = mode === 'sale' ? n.avg_price_sqm_sale : n.avg_price_sqm_rent;
          const nc = priceToColor(np, min, max);
          const arrow = n.trend === 'up' ? '↗' : n.trend === 'down' ? '↘' : '→';
          const tc = n.trend === 'up' ? '#16a34a' : n.trend === 'down' ? '#dc2626' : '#64748b';
          html += `<tr style="border-bottom:1px solid #f1f5f9">
            <td style="padding:4px 6px">${n.name}</td>
            <td style="text-align:right;padding:4px 6px;font-weight:600;color:${nc}">${fmt(np)}</td>
            <td style="text-align:center;padding:4px 6px;color:${tc};font-weight:700">${arrow}</td>
          </tr>`;
        });
        html += '</tbody></table>';
      }
      html += '</div>';
      marker.bindPopup(html, { maxWidth: 300 });
      markers.push(marker);
    });

    return () => markers.forEach((m) => map.removeLayer(m));
  }, [cities, mode, map, stats]);

  return null;
};

const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

const ICON_MAP = {
  monument: Landmark, mosque: Landmark, museum: Palette, park: TreePine,
  historic: Landmark, culture: Palette, ecole: GraduationCap, college: GraduationCap,
  lycee: GraduationCap, universite: GraduationCap, formation: BookOpen,
  sport: Dumbbell, loisir: TreePine, shopping: ShoppingBag, restaurant: UtensilsCrossed,
};
const getIcon = (type) => ICON_MAP[type] || Landmark;

export default function PriceMapPage() {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [mode, setMode] = useState('sale');
  const [loading, setLoading] = useState(true);

  const isFr = language === 'fr';

  useSEO({
    title: isFr ? 'Prix de l\'immobilier en Algérie - Beytam' : 'أسعار العقارات في الجزائر - بيتم',
    description: isFr
      ? 'Carte interactive des prix immobiliers en Algérie. Comparez les prix au m² par ville et par quartier.'
      : 'خريطة تفاعلية لأسعار العقارات في الجزائر. قارن أسعار المتر المربع حسب المدينة والحي.',
    ogTitle: isFr ? 'Prix de l\'immobilier en Algérie' : 'أسعار العقارات في الجزائر',
  });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/price-map`).then((r) => r.json()),
      fetch(`${API}/api/price-history`).then((r) => r.json()),
    ])
      .then(([mapData, histData]) => {
        setData(mapData);
        setHistory(histData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="price-map-loading">
        <div className="animate-spin w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;
  const { cities, stats } = data;

  // Compute global min/max including neighborhoods for color scale
  let allMinSale = stats.min_sale, allMaxSale = stats.max_sale;
  let allMinRent = stats.min_rent, allMaxRent = stats.max_rent;
  cities.forEach((c) => {
    (c.neighborhoods || []).forEach((n) => {
      allMinSale = Math.min(allMinSale, n.avg_price_sqm_sale);
      allMaxSale = Math.max(allMaxSale, n.avg_price_sqm_sale);
      allMinRent = Math.min(allMinRent, n.avg_price_sqm_rent);
      allMaxRent = Math.max(allMaxRent, n.avg_price_sqm_rent);
    });
  });
  const colorStats = { ...stats, min_sale: allMinSale, max_sale: allMaxSale, min_rent: allMinRent, max_rent: allMaxRent };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="price-map-page">
      {/* ─── HERO ─── */}
      <div className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-5 h-5 text-blue-300" />
                <span className="text-blue-300 text-sm font-medium uppercase tracking-wider">
                  {isFr ? 'Observatoire des prix' : 'مرصد الأسعار'}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight" data-testid="price-map-title">
                {isFr ? 'Prix de l\'immobilier en Algérie' : 'أسعار العقارات في الجزائر'}
              </h1>
              <p className="text-blue-200 text-base sm:text-lg max-w-xl">
                {isFr
                  ? 'Comparez les prix au m² dans les principales villes et quartiers du pays.'
                  : 'قارن أسعار المتر المربع في المدن والأحياء الرئيسية في البلاد.'}
              </p>
            </div>
            {/* Toggle Vente / Location */}
            <div className="flex bg-blue-800/50 rounded-full p-1 backdrop-blur-sm border border-blue-700/50" data-testid="price-mode-toggle">
              <button
                onClick={() => setMode('sale')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${mode === 'sale' ? 'bg-white text-blue-900 shadow-lg' : 'text-blue-200 hover:text-white'}`}
                data-testid="toggle-sale"
              >{isFr ? 'Vente' : 'بيع'}</button>
              <button
                onClick={() => setMode('rent')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${mode === 'rent' ? 'bg-white text-blue-900 shadow-lg' : 'text-blue-200 hover:text-white'}`}
                data-testid="toggle-rent"
              >{isFr ? 'Location' : 'إيجار'}</button>
            </div>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
            {[
              { label: isFr ? 'Prix moyen national' : 'المتوسط الوطني', val: mode === 'sale' ? stats.avg_sale : stats.avg_rent, unit: 'DA/m²' },
              { label: isFr ? 'Prix le plus bas' : 'أدنى سعر', val: mode === 'sale' ? stats.min_sale : stats.min_rent, unit: 'DA/m²' },
              { label: isFr ? 'Prix le plus haut' : 'أعلى سعر', val: mode === 'sale' ? stats.max_sale : stats.max_rent, unit: 'DA/m²' },
              { label: isFr ? 'Villes analysées' : 'مدن محلّلة', val: stats.city_count, unit: isFr ? 'villes' : 'مدن', raw: true },
            ].map((s) => (
              <div key={s.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-blue-300 text-xs mb-1">{s.label}</p>
                <p className="text-xl sm:text-2xl font-bold">{s.raw ? s.val : fmt(s.val)}</p>
                <p className="text-blue-300 text-xs">{s.unit}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── MAP + LEGEND ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100" data-testid="price-map-container">
          <div className="flex flex-wrap items-center gap-2 px-5 py-3 bg-slate-50 border-b border-slate-100">
            <MapIcon className="w-4 h-4 text-blue-900" />
            <span className="text-sm font-semibold text-slate-700">
              {isFr ? 'Carte des prix au m²' : 'خريطة أسعار المتر المربع'}
            </span>
            {/* Color legend */}
            <div className="flex items-center gap-1.5 ml-auto" data-testid="color-legend">
              <span className="text-xs text-slate-400">{isFr ? 'Moins cher' : 'أرخص'}</span>
              <div className="flex h-3 rounded-full overflow-hidden w-28 sm:w-36">
                <div className="flex-1" style={{ background: '#22c55e' }} />
                <div className="flex-1" style={{ background: '#14b8a6' }} />
                <div className="flex-1" style={{ background: '#eab308' }} />
                <div className="flex-1" style={{ background: '#f97316' }} />
                <div className="flex-1" style={{ background: '#ef4444' }} />
              </div>
              <span className="text-xs text-slate-400">{isFr ? 'Plus cher' : 'أغلى'}</span>
            </div>
          </div>
          <div style={{ height: '500px' }} data-testid="price-map-leaflet">
            <MapContainer
              center={[35.8, 3.0]}
              zoom={7}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <CityBubbles cities={cities} mode={mode} stats={colorStats} />
            </MapContainer>
          </div>
        </div>
      </div>

      {/* ─── PRICE TABLE ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-6" data-testid="price-table-title">
          {isFr ? 'Prix par ville et par quartier' : 'الأسعار حسب المدينة والحي'}
        </h2>
        <div className="space-y-4">
          {cities.map((city) => (
            <CityPriceCard key={city.city} city={city} mode={mode} isFr={isFr} stats={colorStats} />
          ))}
        </div>
      </div>

      {/* ─── EVOLUTION ANALYSIS ─── */}
      <EvolutionAnalysis cities={cities} stats={stats} isFr={isFr} history={history} />

      {/* ─── CITY GUIDES ─── */}
      <CityGuides cities={cities} isFr={isFr} language={language} />
    </div>
  );
}

/* ━━━ City Price Card (table row) ━━━ */
function CityPriceCard({ city, mode, isFr, stats }) {
  const [expanded, setExpanded] = useState(false);
  const price = mode === 'sale' ? city.avg_price_sqm_sale : city.avg_price_sqm_rent;
  const neighborhoods = city.neighborhoods || [];
  const min = mode === 'sale' ? stats.min_sale : stats.min_rent;
  const max = mode === 'sale' ? stats.max_sale : stats.max_rent;
  const color = priceToColor(price, min, max);

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`city-card-${city.city}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`city-toggle-${city.city}`}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
            <div className="w-4 h-4 rounded-full" style={{ background: color }} />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-lg">{isFr ? city.city : city.city_ar || city.city}</h3>
            <p className="text-sm text-slate-500">{neighborhoods.length} {isFr ? 'quartiers' : 'أحياء'}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-bold text-lg" style={{ color }}>{fmt(price)}</p>
            <p className="text-xs text-slate-400">DA/m²</p>
          </div>
          <ArrowUpRight className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {expanded && neighborhoods.length > 0 && (
        <div className="border-t border-slate-100 px-5 pb-5">
          <div className="overflow-x-auto">
            <table className="w-full mt-3" data-testid={`neighborhood-table-${city.city}`}>
              <thead>
                <tr className="text-xs text-slate-400 uppercase tracking-wider">
                  <th className="text-left pb-3 font-medium">{isFr ? 'Quartier' : 'الحي'}</th>
                  <th className="text-right pb-3 font-medium">{isFr ? 'Vente /m²' : 'بيع /م²'}</th>
                  <th className="text-right pb-3 font-medium">{isFr ? 'Location /m²' : 'إيجار /م²'}</th>
                  <th className="text-center pb-3 font-medium">{isFr ? 'Tendance' : 'الاتجاه'}</th>
                </tr>
              </thead>
              <tbody>
                {neighborhoods.map((n) => (
                  <tr key={n.name} className="border-t border-slate-50">
                    <td className="py-3 text-sm font-medium text-slate-700">{isFr ? n.name : n.name_ar || n.name}</td>
                    <td className="py-3 text-sm font-semibold text-right" style={{ color: priceToColor(n.avg_price_sqm_sale, stats.min_sale, stats.max_sale) }}>
                      {fmt(n.avg_price_sqm_sale)}
                    </td>
                    <td className="py-3 text-sm font-semibold text-right" style={{ color: priceToColor(n.avg_price_sqm_rent, stats.min_rent, stats.max_rent) }}>
                      {fmt(n.avg_price_sqm_rent)}
                    </td>
                    <td className="py-3 text-center"><TrendIcon trend={n.trend} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-3">
            <Link to={`/search?city=${city.city}&transaction_type=vente`} className="text-sm text-blue-700 hover:text-blue-900 font-medium flex items-center gap-1" data-testid={`search-link-sale-${city.city}`}>
              {isFr ? `Acheter à ${city.city}` : `شراء في ${city.city_ar || city.city}`} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <Link to={`/search?city=${city.city}&transaction_type=location`} className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1" data-testid={`search-link-rent-${city.city}`}>
              {isFr ? `Louer à ${city.city}` : `إيجار في ${city.city_ar || city.city}`} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/* ━━━ Evolution / Market Analysis Section with Charts ━━━ */
const CITY_COLORS = {
  'Alger': '#1e40af', 'Oran': '#b45309', 'Béjaïa': '#047857',
  'Tizi Ouzou': '#7c3aed', 'Annaba': '#be123c',
};

function EvolutionAnalysis({ cities, stats, isFr, history }) {
  const [chartMode, setChartMode] = useState('sale');
  const historyData = history?.history;
  const meta = history?.meta;

  // Build chart data: [{year:2019, Alger:280000, Oran:200000, ...}, ...]
  const chartData = [];
  if (historyData) {
    const years = historyData[Object.keys(historyData)[0]]?.[chartMode]?.map((e) => e.year) || [];
    years.forEach((year) => {
      const row = { year };
      Object.entries(historyData).forEach(([city, data]) => {
        const entry = data[chartMode]?.find((e) => e.year === year);
        if (entry) row[city] = entry.price;
      });
      chartData.push(row);
    });
  }

  // Build variation data for bar chart (latest year vs first year)
  const variationData = [];
  if (historyData) {
    Object.entries(historyData).forEach(([city, data]) => {
      const arr = data[chartMode] || [];
      if (arr.length >= 2) {
        const first = arr[0].price;
        const last = arr[arr.length - 1].price;
        const pct = (((last - first) / first) * 100).toFixed(1);
        variationData.push({ city, variation: parseFloat(pct), price: last });
      }
    });
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-3 text-sm">
        <p className="font-bold text-slate-800 mb-1.5">{label}</p>
        {payload.map((p) => (
          <div key={p.name} className="flex items-center gap-2 mb-0.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
            <span className="text-slate-600">{p.name}:</span>
            <span className="font-semibold" style={{ color: p.color }}>
              {fmt(p.value)} {chartMode === 'sale' ? 'DA/m²' : 'DA/m²/mois'}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white border-t border-slate-100" data-testid="evolution-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-blue-900" />
              <span className="text-blue-900 text-sm font-medium uppercase tracking-wider">
                {isFr ? 'Analyse du marché' : 'تحليل السوق'}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="evolution-title">
              {isFr ? 'Évolution des prix 2019 – 2025' : 'تطور الأسعار 2019 – 2025'}
            </h2>
          </div>
          {/* Chart mode toggle */}
          <div className="flex bg-slate-100 rounded-full p-1" data-testid="chart-mode-toggle">
            <button
              onClick={() => setChartMode('sale')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${chartMode === 'sale' ? 'bg-blue-900 text-white shadow' : 'text-slate-600 hover:text-slate-800'}`}
              data-testid="chart-toggle-sale"
            >{isFr ? 'Achat' : 'شراء'}</button>
            <button
              onClick={() => setChartMode('rent')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${chartMode === 'rent' ? 'bg-amber-600 text-white shadow' : 'text-slate-600 hover:text-slate-800'}`}
              data-testid="chart-toggle-rent"
            >{isFr ? 'Location' : 'إيجار'}</button>
          </div>
        </div>

        {/* ── Main Line Chart ── */}
        {chartData.length > 0 && (
          <div className="bg-slate-50 rounded-2xl p-4 sm:p-6 border border-slate-200 mb-8" data-testid="price-line-chart">
            <h3 className="font-bold text-slate-700 mb-1 text-sm">
              {chartMode === 'sale'
                ? (isFr ? 'Prix de vente au m² par ville (DA)' : 'سعر البيع للمتر المربع حسب المدينة (دج)')
                : (isFr ? 'Loyer mensuel au m² par ville (DA)' : 'الإيجار الشهري للمتر المربع حسب المدينة (دج)')}
            </h3>
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="year" tick={{ fontSize: 13, fill: '#64748b' }} />
                <YAxis
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickFormatter={(v) => chartMode === 'sale' ? `${(v / 1000).toFixed(0)}k` : fmt(v)}
                  width={55}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 13 }} />
                {Object.keys(CITY_COLORS).map((city) => (
                  <Line
                    key={city}
                    type="monotone"
                    dataKey={city}
                    stroke={CITY_COLORS[city]}
                    strokeWidth={2.5}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── Variation Bar Chart ── */}
        {variationData.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <div className="bg-slate-50 rounded-2xl p-4 sm:p-6 border border-slate-200" data-testid="variation-bar-chart">
              <h3 className="font-bold text-slate-700 mb-1 text-sm">
                {isFr ? 'Variation totale 2019 → 2025 (%)' : 'التغير الإجمالي 2019 → 2025 (%)'}
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={variationData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="city" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => `${v}%`} width={45} />
                  <Tooltip formatter={(v) => [`+${v}%`, isFr ? 'Variation' : 'التغير']} />
                  <Bar dataKey="variation" radius={[6, 6, 0, 0]}>
                    {variationData.map((d) => (
                      <Cell key={d.city} fill={CITY_COLORS[d.city]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Sale summary */}
              <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-900 flex items-center justify-center">
                    <TrendingUp className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h4 className="font-bold text-blue-900 text-sm">{isFr ? 'Marché achat' : 'سوق الشراء'}</h4>
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-between"><span className="text-xs text-slate-500">{isFr ? 'Moy. nationale' : 'المتوسط'}</span><span className="text-sm font-bold text-blue-900">{fmt(stats.avg_sale)} DA</span></div>
                  <div className="flex justify-between"><span className="text-xs text-slate-500">{isFr ? 'Fourchette' : 'النطاق'}</span><span className="text-xs font-semibold text-slate-700">{fmt(stats.min_sale)} — {fmt(stats.max_sale)}</span></div>
                  {cities.map((c) => {
                    const up = c.neighborhoods.filter((n) => n.trend === 'up').length;
                    return (
                      <div key={c.city} className="flex justify-between items-center"><span className="text-xs text-slate-600">{c.city}</span><span className="text-xs font-bold text-blue-900">{fmt(c.avg_price_sqm_sale)}{up > 0 && <span className="text-emerald-600 ml-1">{up}↗</span>}</span></div>
                    );
                  })}
                </div>
              </div>
              {/* Rent summary */}
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-600 flex items-center justify-center">
                    <TrendingUp className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h4 className="font-bold text-amber-900 text-sm">{isFr ? 'Marché locatif' : 'سوق الإيجار'}</h4>
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-between"><span className="text-xs text-slate-500">{isFr ? 'Moy. nationale' : 'المتوسط'}</span><span className="text-sm font-bold text-amber-900">{fmt(stats.avg_rent)} DA</span></div>
                  <div className="flex justify-between"><span className="text-xs text-slate-500">{isFr ? 'Fourchette' : 'النطاق'}</span><span className="text-xs font-semibold text-slate-700">{fmt(stats.min_rent)} — {fmt(stats.max_rent)}</span></div>
                  {cities.map((c) => {
                    const up = c.neighborhoods.filter((n) => n.trend === 'up').length;
                    return (
                      <div key={c.city} className="flex justify-between items-center"><span className="text-xs text-slate-600">{c.city}</span><span className="text-xs font-bold text-amber-900">{fmt(c.avg_price_sqm_rent)}{up > 0 && <span className="text-emerald-600 ml-1">{up}↗</span>}</span></div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data source info */}
        {meta && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid="data-sources">
            <div className="flex items-center gap-1"><Info className="w-3.5 h-3.5" /><span className="font-medium text-slate-500">{isFr ? 'Sources' : 'المصادر'}:</span></div>
            <span>{meta.sources?.join(' · ')}</span>
            <span className="ml-auto flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {isFr ? `Mis à jour le ${meta.last_updated} · Prochaine MAJ : ${meta.next_update}` : `آخر تحديث: ${meta.last_updated}`}
            </span>
          </div>
        )}

        {/* Trend Summary */}
        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 mt-8" data-testid="trend-summary">
          <h3 className="font-bold text-slate-800 mb-4">{isFr ? 'Synthèse des tendances par quartier' : 'ملخص اتجاهات الأحياء'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((c) => {
              const up = c.neighborhoods.filter((n) => n.trend === 'up').length;
              const stable = c.neighborhoods.filter((n) => n.trend === 'stable').length;
              const down = c.neighborhoods.filter((n) => n.trend === 'down').length;
              const tot = c.neighborhoods.length;
              return (
                <div key={c.city} className="bg-white rounded-xl p-4 border border-slate-100" data-testid={`trend-card-${c.city}`}>
                  <h4 className="font-bold text-slate-800 mb-3">{c.city}</h4>
                  <div className="flex gap-1 h-2.5 rounded-full overflow-hidden mb-3">
                    {up > 0 && <div className="bg-emerald-500 rounded-full" style={{ width: `${(up / tot) * 100}%` }} />}
                    {stable > 0 && <div className="bg-slate-300 rounded-full" style={{ width: `${(stable / tot) * 100}%` }} />}
                    {down > 0 && <div className="bg-red-500 rounded-full" style={{ width: `${(down / tot) * 100}%` }} />}
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-emerald-600 font-medium">{up} {isFr ? 'en hausse' : 'صعود'}</span>
                    <span className="text-slate-400">{stable} {isFr ? 'stable' : 'مستقر'}</span>
                    <span className="text-red-500 font-medium">{down} {isFr ? 'en baisse' : 'نزول'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ━━━ City Guides at Bottom ━━━ */
function CityGuides({ cities, isFr, language }) {
  const [selectedCity, setSelectedCity] = useState(cities[0]?.city || '');
  const city = cities.find((c) => c.city === selectedCity);

  if (!city) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="city-guides-section">
      <div className="flex items-center gap-2 mb-2">
        <Landmark className="w-5 h-5 text-blue-900" />
        <span className="text-blue-900 text-sm font-medium uppercase tracking-wider">
          {isFr ? 'Guides des quartiers' : 'أدلة الأحياء'}
        </span>
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2" data-testid="guides-title">
        {isFr ? 'Découvrez les quartiers en détail' : 'اكتشف الأحياء بالتفصيل'}
      </h2>
      <p className="text-slate-500 mb-6">
        {isFr
          ? 'Monuments, écoles, activités, transport : tout savoir pour choisir votre quartier.'
          : 'المعالم، المدارس، الأنشطة، المواصلات: كل ما تحتاج معرفته لاختيار حيك.'}
      </p>

      {/* City tabs */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="city-guide-tabs">
        {cities.map((c) => (
          <button
            key={c.city}
            onClick={() => setSelectedCity(c.city)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              selectedCity === c.city
                ? 'bg-blue-900 text-white shadow-md'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-900'
            }`}
            data-testid={`guide-tab-${c.city}`}
          >{isFr ? c.city : c.city_ar || c.city}</button>
        ))}
      </div>

      {/* City description */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8 shadow-sm">
        <h3 className="font-bold text-xl text-slate-800 mb-2">{isFr ? city.city : city.city_ar}</h3>
        <p className="text-slate-600 leading-relaxed">
          {isFr ? city.description : city.description_ar || city.description}
        </p>
      </div>

      {/* Neighborhood guide cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {city.neighborhoods.map((n) => (
          <NeighborhoodGuideCard key={n.name} neighborhood={n} isFr={isFr} language={language} cityName={city.city} />
        ))}
      </div>

      {/* Link to full city guide */}
      <div className="text-center mt-8">
        <Link to={`/cities/${city.city.toLowerCase()}`}>
          <button className="inline-flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors shadow-md" data-testid={`full-guide-${city.city}`}>
            {isFr ? `Guide complet de ${city.city}` : `الدليل الكامل لـ ${city.city_ar || city.city}`}
            <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </div>
  );
}

/* ━━━ Neighborhood Guide Card ━━━ */
function NeighborhoodGuideCard({ neighborhood: n, isFr, language, cityName }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`guide-card-${n.name}`}>
      {n.image && (
        <div className="h-40 relative overflow-hidden">
          <img src={n.image} alt={n.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4">
            <h4 className="font-bold text-white text-lg">{n.name}</h4>
            {n.name_ar && <p className="text-white/70 text-xs">{n.name_ar}</p>}
          </div>
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 rounded-full px-2 py-1">
            <TrendIcon trend={n.trend} />
            <span className="text-xs font-medium text-slate-700">
              {n.trend === 'up' ? (isFr ? 'Hausse' : 'صعود') : n.trend === 'down' ? (isFr ? 'Baisse' : 'نزول') : (isFr ? 'Stable' : 'مستقر')}
            </span>
          </div>
        </div>
      )}

      <div className="p-4">
        {!n.image && (
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-bold text-slate-800">{n.name}</h4>
            <div className="flex items-center gap-1"><TrendIcon trend={n.trend} /></div>
          </div>
        )}

        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
          {language === 'ar' && n.description_ar ? n.description_ar : n.description}
        </p>

        {/* Prices */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-blue-50 rounded-lg p-2.5">
            <p className="text-[10px] text-blue-600 font-medium">{isFr ? 'Achat /m²' : 'شراء /م²'}</p>
            <p className="font-bold text-blue-900 text-sm">{fmt(n.avg_price_sqm_sale)} DA</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-2.5">
            <p className="text-[10px] text-amber-600 font-medium">{isFr ? 'Location /m²' : 'إيجار /م²'}</p>
            <p className="font-bold text-amber-900 text-sm">{fmt(n.avg_price_sqm_rent)} DA</p>
          </div>
        </div>

        {/* Ambiance & Transport */}
        {n.ambiance && (
          <div className="bg-slate-50 rounded-lg p-2.5 mb-2">
            <p className="text-[10px] text-slate-500 font-medium flex items-center gap-1"><TreePine className="w-3 h-3" />{isFr ? 'Ambiance' : 'الأجواء'}</p>
            <p className="text-xs text-slate-700 mt-0.5 line-clamp-2">{language === 'ar' && n.ambiance_ar ? n.ambiance_ar : n.ambiance}</p>
          </div>
        )}
        {n.transport && (
          <div className="bg-slate-50 rounded-lg p-2.5 mb-3">
            <p className="text-[10px] text-slate-500 font-medium flex items-center gap-1"><Bus className="w-3 h-3" />{isFr ? 'Transport' : 'المواصلات'}</p>
            <p className="text-xs text-slate-700 mt-0.5 line-clamp-2">{language === 'ar' && n.transport_ar ? n.transport_ar : n.transport}</p>
          </div>
        )}

        {/* POI toggle */}
        {(n.monuments?.length > 0 || n.schools?.length > 0 || n.activities?.length > 0) && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between text-xs font-semibold text-blue-900 hover:text-blue-700 py-1.5"
              data-testid={`expand-guide-${n.name}`}
            >
              {isFr ? 'Points d\'intérêt' : 'نقاط الاهتمام'}
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {expanded && (
              <div className="space-y-3 mt-1">
                <POISection items={n.monuments} title={isFr ? 'Monuments' : 'المعالم'} icon={Landmark} color="rose" />
                <POISection items={n.schools} title={isFr ? 'Éducation' : 'التعليم'} icon={GraduationCap} color="indigo" />
                <POISection items={n.activities} title={isFr ? 'Activités' : 'الأنشطة'} icon={Dumbbell} color="emerald" />
              </div>
            )}
          </>
        )}

        <Link to={`/search?city=${cityName}&neighborhood=${n.name}`} className="block mt-3">
          <button className="w-full text-xs font-medium text-blue-700 hover:text-blue-900 border border-blue-200 hover:border-blue-300 rounded-lg py-2 flex items-center justify-center gap-1 transition-colors">
            {isFr ? 'Voir les annonces' : 'عرض الإعلانات'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </Link>
      </div>
    </div>
  );
}

function POISection({ items, title, icon: Icon, color }) {
  if (!items || items.length === 0) return null;
  const bgMap = { rose: 'bg-rose-50', indigo: 'bg-indigo-50', emerald: 'bg-emerald-50' };
  const textMap = { rose: 'text-rose-500', indigo: 'text-indigo-500', emerald: 'text-emerald-500' };
  return (
    <div>
      <p className={`text-[10px] font-semibold ${textMap[color]} flex items-center gap-1 mb-1.5`}>
        <Icon className="w-3 h-3" /> {title}
      </p>
      <div className="space-y-1">
        {items.map((item, i) => {
          const ItemIcon = getIcon(item.type);
          return (
            <div key={i} className={`flex items-start gap-1.5 ${bgMap[color]}/60 rounded-lg p-2`}>
              <ItemIcon className={`w-3 h-3 ${textMap[color]} mt-0.5 flex-shrink-0`} />
              <div>
                <p className="text-xs font-medium text-slate-800">{item.name}</p>
                {item.description && <p className="text-[10px] text-slate-500 mt-0.5">{item.description}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
